<script lang="ts">
  import { wbsLabel } from "../lib/wbs";
  import {
    tasks, det, wbsNodes, allowWeekendWork, projectStartDate, floorDay,
    leanView, selectedDay, openEditModal, baselineSetAt, setBaselineNow, canEdit, projectPhases, twoShifts,
  } from "../lib/stores";
  import { phaseColor } from "../lib/phases";
  import { buildSummary } from "../lib/summary";
  import { formatCivilDate, projectDayLabel, weekOfProjectDay } from "../lib/projectDate";
  import { fmt1 } from "../lib/format";

  $: result = $det;
  $: summary = result.ok ? buildSummary($tasks, result, $allowWeekendWork, $floorDay, $projectPhases, $twoShifts ? 2 : 1) : null;

  /** A block standing this long is called out rather than just listed. */
  const STALE_BLOCK_DAYS = 3;

  function dayLabel(day: number): string {
    return projectDayLabel($projectStartDate, day);
  }
  /** projectEnd is an exclusive boundary — work finishes at the *end* of the day before
   *  it — so the readout names that day rather than the boundary. */
  function lastDay(end: number): number {
    return Math.max(0, end - 1);
  }
  function driftLabel(d: number | null): string {
    if (d == null) return "—";
    if (d === 0) return "on plan";
    return d > 0 ? `+${d} d late` : `${d} d early`;
  }
  function rangeLabel(start: number | null, finish: number | null): string {
    if (start == null) return "—";
    if (finish == null) return `${dayLabel(start)} →`;
    return `${dayLabel(start)} – ${dayLabel(finish)}`;
  }
  const STATUS_LABEL: Record<string, string> = { "not started": "Not started", "in progress": "In progress", complete: "Complete" };
  function openDay(day: number) {
    selectedDay.set(day);
    leanView.set("today");
  }
  $: editable = $canEdit;
</script>

{#if !summary}
  <div class="today-empty">Fix the schedule error above to see the summary.</div>
{:else}
  <div class="summary">
    <div class="kpis">
      <div class="kpi">
        <div class="kpi-label">Forecast finish</div>
        <div class="kpi-value">{dayLabel(lastDay(summary.forecastEnd))}</div>
        <div class="kpi-note">
          Day {lastDay(summary.forecastEnd)} · week {weekOfProjectDay(lastDay(summary.forecastEnd))}
        </div>
      </div>

      <div class="kpi" class:bad={(summary.drift ?? 0) > 0} class:good={(summary.drift ?? 0) < 0}>
        <div class="kpi-label">Against baseline</div>
        <div class="kpi-value">{driftLabel(summary.drift)}</div>
        <div class="kpi-note">
          {#if summary.baselineEnd != null}
            Baseline finished {dayLabel(lastDay(summary.baselineEnd))}
          {:else}
            No baseline could be computed
          {/if}
        </div>
        <div class="kpi-note">
          {#if $baselineSetAt}
            Baseline set {formatCivilDate($baselineSetAt)}
          {:else}
            Baseline never captured — using each activity's estimate from when it was added
          {/if}
        </div>
        {#if editable}
          <button class="linkish kpi-action" type="button" on:click={setBaselineNow}>
            {$baselineSetAt ? "Re-set baseline to current plan" : "Set baseline to current plan"}
          </button>
        {/if}
      </div>

      <div class="kpi">
        <div class="kpi-label">Work done</div>
        <div class="kpi-value">{summary.percentDone}%</div>
        <div class="kpi-note">{summary.doneDays} of {summary.totalDays} activity-days</div>
      </div>

      <div class="kpi" class:bad={summary.blocked.length > 0}>
        <div class="kpi-label">Blocked</div>
        <div class="kpi-value">{summary.blocked.length}</div>
        <div class="kpi-note">
          {#if summary.blocked.length}
            {summary.blocked.filter((b) => (b.age ?? 0) >= STALE_BLOCK_DAYS).length} standing {STALE_BLOCK_DAYS}+ days
          {:else}
            Nothing is waiting on anything
          {/if}
        </div>
      </div>
    </div>

    {#if summary.blocked.length}
      <section class="summary-block">
        <h3 class="summary-head">Blocked work</h3>
        <ul class="blocked-list">
          {#each summary.blocked as row (row.task.id)}
            <li class="blocked-row" class:stale={(row.age ?? 0) >= STALE_BLOCK_DAYS}>
              <button class="linkish" type="button" on:click={() => openEditModal(row.task.id)}>
                <span class="mono">{wbsLabel(row.task, $wbsNodes)}</span> {row.task.name}
              </button>
              <span class="blocked-why">{row.task.blockedReason || "No reason recorded"}</span>
              <span class="blocked-age">
                {#if row.age == null}—{:else if row.age === 0}raised today{:else}{row.age} d{/if}
              </span>
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    <section class="summary-block">
      <h3 class="summary-head">Milestones</h3>
      {#if !summary.milestones.length}
        <p class="summary-empty">No milestones in the plan.</p>
      {:else}
        <table class="summary-table">
          <thead>
            <tr>
              <th>Milestone</th>
              <th>Forecast</th>
              <th>Baseline</th>
              <th>Drift</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {#each summary.milestones as m (m.task.id)}
              <tr class:done={m.complete}>
                <td>
                  <button class="linkish" type="button" on:click={() => openDay(m.day)}>
                    <span class="mono">{wbsLabel(m.task, $wbsNodes)}</span> {m.task.name}
                  </button>
                </td>
                <td class="mono">{dayLabel(m.day)}</td>
                <td class="mono muted">{m.baselineDay == null ? "—" : dayLabel(m.baselineDay)}</td>
                <td class="mono" class:bad={(m.drift ?? 0) > 0} class:good={(m.drift ?? 0) < 0}>
                  {driftLabel(m.drift)}
                </td>
                <td>
                  {#if m.complete}
                    <span class="pill done">Done</span>
                  {:else if m.task.blocked}
                    <span class="pill blocked">Blocked</span>
                  {:else if m.critical}
                    <span class="pill crit">Critical</span>
                  {:else}
                    <span class="pill">Upcoming</span>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </section>

    <section class="summary-block">
      <h3 class="summary-head">
        What's driving the date
        <span class="summary-sub">{summary.criticalPath.length} activities · {summary.criticalDays} days of work</span>
      </h3>
      {#if !summary.criticalPath.length}
        <p class="summary-empty">Nothing is on the critical path — every remaining activity has slack.</p>
      {:else}
        <ol class="critical-chain">
          {#each summary.criticalPath as t (t.id)}
            <li>
              <button class="chain-item" type="button" on:click={() => openEditModal(t.id)}>
                <span class="chain-dot" style="background:{phaseColor($projectPhases, t.phase)}"></span>
                <span class="mono">{wbsLabel(t, $wbsNodes)}</span>
                <span class="chain-name">{t.name}</span>
                {#if t.type !== "milestone"}<span class="chain-days">{fmt1(t.m / ($twoShifts ? 2 : 1))} d</span>{/if}
              </button>
            </li>
          {/each}
        </ol>
      {/if}
    </section>

    <section class="summary-block">
      <h3 class="summary-head">Progress by phase</h3>
      <div class="phase-bars">
        {#each summary.phases as p (p.phase)}
          {@const pct = p.totalDays ? Math.round((p.doneDays / p.totalDays) * 100) : 0}
          <div class="phase-bar">
            <div class="phase-bar-head">
              <span class="chain-dot" style="background:{phaseColor($projectPhases, p.phase)}"></span>
              <span class="phase-bar-name">{p.phase}</span>
              <span class="phase-bar-num">{p.doneDays} / {p.totalDays} d · {pct}%</span>
            </div>
            <div class="phase-track">
              <div class="phase-fill" style="width:{pct}%; background:{phaseColor($projectPhases, p.phase)}"></div>
            </div>
          </div>
        {/each}
      </div>
    </section>

    <section class="summary-block">
      <h3 class="summary-head">Task execution</h3>
      {#if !summary.taskVariance.length}
        <p class="summary-empty">No activities to report on yet.</p>
      {:else}
        <table class="summary-table">
          <thead>
            <tr>
              <th>Activity</th>
              <th>Baseline</th>
              <th>Forecast</th>
              <th>Actual</th>
              <th>Variance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {#each summary.taskVariance as row (row.task.id)}
              {@const variance = row.finishVariance ?? row.startVariance}
              <tr class:done={row.status === "complete"}>
                <td>
                  <button class="linkish" type="button" on:click={() => openEditModal(row.task.id)}>
                    <span class="mono">{wbsLabel(row.task, $wbsNodes)}</span> {row.task.name}
                  </button>
                </td>
                <td class="mono muted">{rangeLabel(row.baselineStart, row.baselineFinish)}</td>
                <td class="mono">{rangeLabel(row.forecastStart, row.forecastFinish)}</td>
                <td class="mono">{rangeLabel(row.actualStart, row.actualFinish)}</td>
                <td class="mono" class:bad={(variance ?? 0) > 0} class:good={(variance ?? 0) < 0}>
                  {driftLabel(variance)}
                </td>
                <td>
                  {#if row.status === "complete"}
                    <span class="pill done">Complete</span>
                  {:else if row.task.blocked}
                    <span class="pill blocked">Blocked</span>
                  {:else}
                    <span class="pill">{STATUS_LABEL[row.status]}</span>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </section>

    <p class="summary-foot">
      Baseline is the same network and pins at each activity's baseline duration, with no
      progress and no today-floor — so the drift shown is the movement caused by how the
      job has actually gone, not by re-estimating it. Use "Set baseline to current plan"
      above right before execution starts to lock in that plan as the baseline; re-running
      it later replaces the baseline with whatever's estimated at that moment. Actual
      start/finish come from days marked done on the Lean board, or from a manual
      override set in an activity's edit modal.
    </p>
  </div>
{/if}
